import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestedBy, TriggerSource } from '@robocorp/robocloud-orchestrator-interface';
import { Avatar, Badge, Box } from '@robocorp/components';
import { IconBolt, IconClock, IconHelpCircle, IconMail } from '@robocorp/icons/iconic';
import { IconLogoRobocorp } from '@robocorp/icons/logos';
import { IconType } from '@robocorp/icons';
import { processRunTriggerTypes } from '~/constants';
import { ExtractProps } from '~/types';
import { styled } from '@robocorp/theme';
import { CellPlaceholder } from './Placeholder';

const Container = styled(Box)`
  svg .light {
    display: none;
  }

  svg .dark {
    display: block;
  }
`;

const triggerSourceType = processRunTriggerTypes;

type Trigger = {
  icon: IconType;
  translationKey: string;
};

const SYSTEM: Trigger = { icon: IconLogoRobocorp, translationKey: 'System' };
const UNKNOWN: Trigger = { icon: IconHelpCircle, translationKey: 'unknown' };
const SCHEDULE: Trigger = { icon: IconClock, translationKey: 'scheduler' };
const API: Trigger = { icon: IconBolt, translationKey: 'API' };
const EMAIL: Trigger = { icon: IconMail, translationKey: 'email' };

type Props = {
  triggerSource?: TriggerSource;
} & Partial<ExtractProps<typeof Avatar>>;

export const CellAvatar: FC<Props> = ({ triggerSource, size = 'small', ...avatarProps }) => {
  const { t } = useTranslation();

  if (!triggerSource) {
    return <CellPlaceholder />;
  }

  const sourceType = triggerSource?.type;
  if (
    (triggerSource?.type === triggerSourceType.MANUAL || triggerSource.type === ('User' as any)) &&
    triggerSource?.details
  ) {
    const { first_name: firstName, last_name: lastName, email, picture } = triggerSource.details;
    const title = [firstName, lastName].filter(Boolean).join(' ');
    return (
      <Avatar
        src={picture}
        alt="Trigger Source"
        placeholder={title}
        title={title}
        subtitle={email}
        size={size}
        {...avatarProps}
      />
    );
  }

  let trigger = UNKNOWN;
  if (sourceType === triggerSourceType.SCHEDULE) {
    trigger = SCHEDULE;
  }
  if (sourceType === triggerSourceType.API) {
    trigger = API;
  }
  if (sourceType === triggerSourceType.EMAIL) {
    trigger = EMAIL;
  }

  const { translationKey, ...badgeProps } = trigger;
  return <Badge label={t(translationKey)} ghost iconColor="content.subtle" {...badgeProps} />;
};

export const CellActorAvatar: FC<{ actor: RequestedBy } & Partial<ExtractProps<typeof Avatar>>> = ({
  actor,
  size = 'small',
  ...avatarProps
}) => {
  const { t } = useTranslation();

  if (!actor) return <CellPlaceholder />;

  if (actor.type === 'User' && actor?.details) {
    const { firstName, lastName, pictureUrl } = actor.details;
    const title = [firstName, lastName].filter(Boolean).join(' ');
    return (
      <Avatar
        src={pictureUrl as string | never}
        alt="Actor"
        placeholder={title}
        title={title}
        size={size}
        {...avatarProps}
      />
    );
  }

  let source = UNKNOWN;
  if (actor.type === 'System' || actor.type === 'ActivityRun') {
    source = SYSTEM;
  }

  const { translationKey, icon: Icon } = source;
  return (
    <Box display="flex" alignItems="center" gap="$8">
      <Container
        padding="$4"
        backgrounds={['grey90', 'grey90']}
        borderRadius="50%"
        height="$24"
        width="$24"
      >
        <Icon size="small" colors={['grey0', 'grey0']} />
      </Container>
      <span>{t(translationKey)}</span>
    </Box>
  );
};
